// Per-section filter persistence (§7B decision 4): each section's filter values
// live in the URL + localStorage under namespaced keys (e.g.
// "leagues.competitionId"), so a section remembers its own choices independently
// and a reload/back-forward restores them. The parse is pure (unit-tested); the
// write touches window and is best-effort.

/** Read a numeric scope param: URL `?<key>=` first, then localStorage, else null.
 *  Pure — the caller passes the URL search string + a storage getter. An
 *  unparseable value in either source is ignored (returns null / falls through). */
export function readScopeNumber(
  search: string,
  getStored: (key: string) => string | null,
  key: string
): number | null {
  const fromUrl = new URLSearchParams(search).get(key);
  const url = toPositiveInt(fromUrl);
  if (url !== null) return url;
  return toPositiveInt(getStored(key));
}

function toPositiveInt(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Read the initial value from the live browser (safe if storage is blocked). */
export function readInitialScopeNumber(key: string): number | null {
  const get = (k: string): string | null => {
    try {
      return window.localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  return readScopeNumber(window.location.search, get, key);
}

/** Persist a numeric scope value to the URL (no history entry) + localStorage.
 *  null clears it. Best-effort — a blocked store never throws. */
export function persistScopeNumber(key: string, value: number | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // storage blocked — the URL still carries it
  }
  const url = new URL(window.location.href);
  if (value === null) url.searchParams.delete(key);
  else url.searchParams.set(key, String(value));
  window.history.replaceState(null, "", url);
}
